import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
// import Badge from '@mui/material/Badge';
// import MessageIcon from '@mui/icons-material/Message';
import MoreIcon from '@mui/icons-material/MoreVert';

import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/auth-context';
import { useMatchCurrentLocationRoutes } from 'hooks/current-path-route-match';
import { NavDrawer } from './nav-drawer';
import { useCustomTheme } from 'contexts';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { NavPages, NavUtils } from 'utils';

interface Props {
    title?: string;
}

export const NavBar = (props: Props) => {
    const navigate = useNavigate();
    const authContext = useAuth();
    const routeMatches = useMatchCurrentLocationRoutes();
    const theme = useCustomTheme();
    const pathMatch = routeMatches ? routeMatches[0] : null;
    const eventAlias = pathMatch?.params.eventAlias;
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState<null | HTMLElement>(null);

    const isMenuOpen = Boolean(anchorEl);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        handleMobileMenuClose();
    };

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    const handleNavigate = (path: string) => {
        if (eventAlias) {
            console.log('navigate to ', `/${eventAlias}${path}`);
            navigate(`/${eventAlias}${path}`);
            return;
        }
        console.log('navigate to ', path);
        navigate(path);
        handleMenuClose();
    };

    const toggleThemeMode = React.useCallback(() => {
        const newMode = theme.palette.mode === 'light' ? 'dark' : 'light';
        theme.setThemeMode(newMode);
    }, [theme]);

    const handleLogout = () => {
        authContext.logout();
        navigate(NavUtils.generateNavPath(NavPages.Root, undefined, eventAlias));
        handleMenuClose();
    };

    const menuId = 'primary-account-menu';
    const renderUserMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right'
            }}
            id={menuId}
            keepMounted
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
            }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            <MenuItem onClick={() => handleNavigate('/profile')}>Profile</MenuItem>
            <MenuItem onClick={handleLogout}>Log out</MenuItem>
            <MenuItem onClick={toggleThemeMode}>
                <Typography>Toggle theme</Typography>
                <IconButton sx={{ ml: 1 }} onClick={toggleThemeMode} color="inherit">
                    {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
            </MenuItem>
        </Menu>
    );

    const mobileMenuId = 'primary-account-menu-mobile';
    const renderMobileMenu = (
        <Menu
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right'
            }}
            id={mobileMenuId}
            keepMounted
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
            }}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
        >
            {/* TODO - future messaging feature */}
            {/* <MenuItem>
                <IconButton size="large" aria-label="show 4 new mails" color="inherit">
                    <Badge badgeContent={4} color="error">
                        <MailIcon />
                    </Badge>
                </IconButton>
                <p>Messages</p>
            </MenuItem> */}
            {authContext.currentUser && (
                <MenuItem onClick={handleProfileMenuOpen}>
                    <IconButton
                        size="large"
                        aria-label="account of current user"
                        aria-controls="primary-account-menu"
                        aria-haspopup="true"
                        color="inherit"
                    >
                        <AccountCircle />
                    </IconButton>
                    <p>Profile</p>
                </MenuItem>
            )}
        </Menu>
    );

    return (
        <>
            <AppBar position="static" className="nav-bar">
                <Toolbar>
                    <NavDrawer eventAlias={eventAlias} />
                    <Typography variant="h6" noWrap component="div" sx={{ display: { xs: 'none', sm: 'block' } }}>
                        {props.title ?? 'Avron Quizzes'}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        {/* TODO - future messaging feature */}
                        {/* <IconButton size="large" aria-label="show 4 new mails" color="inherit">
                            <Badge badgeContent={4} color="error">
                                <MessageIcon />
                            </Badge>
                        </IconButton> */}
                        {authContext.currentUser && (
                            <>
                                <IconButton
                                    size="large"
                                    edge="end"
                                    aria-label="account of current user"
                                    aria-controls={menuId}
                                    aria-haspopup="true"
                                    onClick={handleProfileMenuOpen}
                                    color="inherit"
                                >
                                    <span>{authContext.currentUser.displayName}</span>
                                    &nbsp;
                                    <AccountCircle />
                                </IconButton>
                            </>
                        )}
                    </Box>
                    <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                        {authContext.currentUser && (
                            <IconButton
                                size="large"
                                aria-label="show more"
                                aria-controls={mobileMenuId}
                                aria-haspopup="true"
                                onClick={handleMobileMenuOpen}
                                color="inherit"
                            >
                                <MoreIcon />
                            </IconButton>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>
            {renderMobileMenu}
            {renderUserMenu}
        </>
    );
}
